import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { StatsCard } from "@/components/ui/stats-card";
import { StatusBadge } from "@/components/shared/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Users, Zap, Ticket, ClipboardList, ArrowRight } from "lucide-react";
import { useLocation } from "wouter";
import { formatDate, formatCurrency, formatRelativeTime } from "@/lib/format";
import type { Ticket as TicketType } from "@db/schema";
import type { RecurringBillingQueueItem } from "@shared/contracts";

interface DashboardStats {
  clientCount: number;
  activeServiceCount: number;
  pendingRenewalCount: number;
  totalMrr: number;
}

interface WorkRequestsResponse {
  data: Array<{
    id: string;
    fullName: string;
    company: string;
    category: string;
    status: string;
    createdAt: string;
  }>;
  pagination: { total: number };
}

export default function AdminDashboard() {
  const [, navigate] = useLocation();

  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ["/api/billing/dashboard/stats"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: tickets, isLoading: ticketsLoading } = useQuery<TicketType[]>({
    queryKey: ["/api/support/tickets?status=new,in_progress"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: workRequests, isLoading: wrLoading } =
    useQuery<WorkRequestsResponse>({
      queryKey: ["/api/intake/work-requests?status=submitted&limit=5"],
      queryFn: getQueryFn({ on401: "throw" }),
    });

  const { data: renewals, isLoading: renewalsLoading } = useQuery<
    RecurringBillingQueueItem[]
  >({
    queryKey: ["/api/billing/renewals"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const openTicketCount = tickets?.length ?? 0;
  const pendingWrCount = workRequests?.pagination?.total ?? 0;
  const upcomingRenewals = (renewals ?? [])
    .filter((r) => r.status === "pending" || r.status === "emailed")
    .slice(0, 5);
  const recentTickets = (tickets ?? []).slice(0, 5);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold font-heading">Dashboard</h2>
        <p className="text-muted-foreground">
          Overview of your hosting management system
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[120px] rounded-lg" />
          ))
        ) : (
          <>
            <StatsCard
              title="Active Clients"
              value={stats?.clientCount ?? 0}
              description="Total billing clients"
              icon={Users}
            />
            <StatsCard
              title="Active Services"
              value={stats?.activeServiceCount ?? 0}
              description="Running subscriptions"
              icon={Zap}
            />
            <StatsCard
              title="Open Tickets"
              value={openTicketCount}
              description="New & in progress"
              icon={Ticket}
            />
            <StatsCard
              title="Work Requests"
              value={pendingWrCount}
              description="Pending intake requests"
              icon={ClipboardList}
            />
          </>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upcoming Renewals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold font-heading">
              Upcoming Renewals
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/renewals")}
            >
              View all <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            {renewalsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : upcomingRenewals.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No upcoming renewals
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {upcomingRenewals.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium text-sm">
                        {r.serviceName}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatCurrency(r.amount, r.currency)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(r.dueAt)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge type="renewal" status={r.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Recent Tickets */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold font-heading">
              Recent Tickets
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/tickets")}
            >
              View all <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            {ticketsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : recentTickets.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No open tickets
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentTickets.map((t) => (
                    <TableRow
                      key={t.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/admin/tickets/${t.id}`)}
                    >
                      <TableCell className="font-medium text-sm max-w-[200px] truncate">
                        {t.subject ?? "No subject"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge type="priority" status={t.priority} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge type="ticket" status={t.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatRelativeTime(t.updatedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
